/**
 * Profile — read-only view of the user's saved profile.
 * Route: /app/profile
 *
 * Mirrors the wizard's PreviewStep layout (same SectionPanel + Definition
 * pattern) for visual consistency between "review before save" and
 * "view after save". Industry-standard fintech account-view pattern
 * (Stripe / Razorpay / Mercury): utilitarian, scannable, per-section
 * edit deep-link, document viewer in a modal.
 *
 * What this page deliberately is NOT:
 *   - No hero / cover photo
 *   - No wallet / balance card (that's /app/credit-wallet)
 *   - No verification badges (defer until backend signals are reliable)
 *   - No profile photo upload (deferred)
 *
 * Skill compliance: P6 header, P15 panels, P20 page skeleton, hard rule 6
 * (no hex), no inline <style>, no console.log of PII.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Skeleton } from 'primereact/skeleton';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { classNames } from 'primereact/utils';
import axiosInstance from '../config/axios.config';

/* ── Lookup tables (mirrors the wizard) ─────────────────────────────── */
const USER_TYPE_LABEL = {
    loan_agent: 'Loan Agent',
    dsa: 'DSA',
    financial_consultant: 'Financial Consultant',
    insurance_agent: 'Insurance Agent',
    investment_advisor: 'Investment Advisor',
    tax_consultant: 'Tax Consultant',
    real_estate_agent: 'Real Estate Agent',
    other: 'Other',
};

const LOAN_PRODUCT_LABEL = {
    home_loan: 'Home Loan',
    personal_loan: 'Personal Loan',
    business_loan: 'Business Loan',
    vehicle_loan: 'Vehicle Loan',
    education_loan: 'Education Loan',
    gold_loan: 'Gold Loan',
    credit_card: 'Credit Card',
};

const DISBURSEMENT_LABEL = {
    less_than_1_lakh: 'Less than ₹1 Lakh',
    '1_lakh_to_10_lakh': '₹1 Lakh – ₹10 Lakh',
    '10_lakh_to_50_lakh': '₹10 Lakh – ₹50 Lakh',
    '50_lakh_to_1_crore': '₹50 Lakh – ₹1 Crore',
    '1_to_5_crore': '₹1 – 5 Crore',
    more_than_5_crore: 'More than ₹5 Crore',
};

const ESTABLISHMENT_LABEL = {
    individual: 'Individual',
    proprietorship: 'Proprietorship',
    partnership: 'Partnership',
    llp: 'LLP',
    private_limited: 'Private Limited',
    public_limited: 'Public Limited',
    one_man_company: 'One Person Company',
    society: 'Society',
    trust: 'Trust',
    nbfc: 'NBFC',
    bank: 'Bank',
    mfi: 'MFI',
};

const dash = '—';
const formatList = (arr, dict) => {
    if (!Array.isArray(arr) || arr.length === 0) return dash;
    return arr.map((v) => dict[v] || v).join(', ');
};

const formatDocumentLabel = (fieldName) => {
    if (!fieldName) return '';
    return fieldName
        .replace(/_doc$/, '')
        .replace(/_/g, ' ')
        .split(' ')
        .map((w) => {
            if (w.toLowerCase() === 'pan') return 'PAN';
            if (w.toLowerCase() === 'gst') return 'GST';
            if (w.toLowerCase() === 'aadhar' || w.toLowerCase() === 'aadhaar') return 'Aadhaar';
            if (w.toLowerCase() === 'rbi') return 'RBI';
            if (w.toLowerCase() === 'tan') return 'TAN';
            return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
        })
        .join(' ');
};

/* ════════════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ════════════════════════════════════════════════════════════════════ */
const Profile = () => {
    const navigate = useNavigate();
    const toast = useRef(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [profileData, setProfileData] = useState(null);
    const [viewingDoc, setViewingDoc] = useState(null);     // { id, file_name, document_url, mime/extension }
    const [docUrlLoading, setDocUrlLoading] = useState(false);

    /* ── Load profile ────────────────────────────────────────────────── */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axiosInstance.get('/user-details/profile');
                if (cancelled) return;
                if (res.data?.success) {
                    setProfileData(res.data.data || {});
                } else {
                    setError(res.data?.message || 'Could not load your profile.');
                }
            } catch (e) {
                if (!cancelled) {
                    setError(e.response?.data?.message || "We couldn't load your profile right now. Please try again.");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    /* ── Edit deep-link to wizard ───────────────────────────────────── */
    const editStep = (i) => navigate(`/app/profile/complete?step=${i}`);

    /* ── Document viewer ────────────────────────────────────────────── */
    const handleViewDoc = async (doc) => {
        if (!doc?.id) return;
        setDocUrlLoading(true);
        try {
            const res = await axiosInstance.get(`/user-details/documents/${doc.id}/url`);
            if (res.data?.success && res.data?.data?.url) {
                setViewingDoc({
                    ...doc,
                    document_url: res.data.data.url,
                });
            } else {
                throw new Error(res.data?.message || 'Could not open the document.');
            }
        } catch (e) {
            toast.current?.show({
                severity: 'error',
                summary: "Couldn't open document",
                detail: e.response?.data?.message || e.message || 'Please try again in a moment.',
                life: 4000,
            });
        } finally {
            setDocUrlLoading(false);
        }
    };

    const handleDownloadDoc = async (doc) => {
        if (!doc?.id) return;
        try {
            const res = await axiosInstance.get(`/user-details/documents/${doc.id}/url`);
            if (res.data?.success && res.data?.data?.url) {
                const link = document.createElement('a');
                link.href = res.data.data.url;
                link.download = doc.file_name || 'document';
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (e) {
            toast.current?.show({
                severity: 'error',
                summary: "Couldn't download",
                detail: e.response?.data?.message || 'Please try again.',
                life: 4000,
            });
        }
    };

    /* ── Render — loading ───────────────────────────────────────────── */
    if (loading) {
        return (
            <div className="grid">
                <div className="col-12">
                    <div className="mb-4">
                        <Skeleton width="10rem" height="1.5rem" className="mb-2" />
                        <Skeleton width="20rem" height="1rem" />
                    </div>
                    {/* Hero skeleton */}
                    <div className="surface-ground border-round p-3 mb-3">
                        <div className="flex align-items-center gap-3 mb-3">
                            <Skeleton shape="circle" size="4rem" />
                            <div className="flex-1">
                                <Skeleton width="14rem" height="1.25rem" className="mb-2" />
                                <Skeleton width="8rem" height="1rem" />
                            </div>
                            <Skeleton width="6rem" height="2rem" borderRadius="6px" />
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Skeleton width="7rem" height="1.5rem" borderRadius="2rem" />
                            <Skeleton width="9rem" height="1.5rem" borderRadius="2rem" />
                            <Skeleton width="11rem" height="1.5rem" borderRadius="2rem" />
                        </div>
                    </div>
                    {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="surface-ground border-1 surface-border border-round p-3 mb-3">
                            <div className="flex align-items-center gap-2 mb-3">
                                <Skeleton shape="square" size="2rem" />
                                <Skeleton width="10rem" height="1rem" />
                            </div>
                            <div className="grid">
                                {[1, 2, 3, 4].map((k) => (
                                    <div key={k} className="col-6 md:col-3 mb-2">
                                        <Skeleton width="60%" height="0.6rem" className="mb-1" />
                                        <Skeleton width="80%" height="0.875rem" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    /* ── Render — error ─────────────────────────────────────────────── */
    if (error) {
        return (
            <div className="grid">
                <div className="col-12">
                    <div className="mb-4">
                        <h1 className="text-xl font-semibold text-900 m-0 mb-1">Profile</h1>
                    </div>
                    <Message severity="error" text={error} className="w-full" />
                    <div className="mt-3">
                        <Button
                            label="Try again"
                            icon="pi pi-refresh"
                            size="small"
                            outlined
                            onClick={() => window.location.reload()}
                        />
                    </div>
                </div>
            </div>
        );
    }

    /* ── Derived values ─────────────────────────────────────────────── */
    const data = profileData || {};
    const basic = data.basic_info || {};
    const userType = data.user_type || {};
    const loanAgent = data.loan_agent_details || {};
    const establishment = data.establishment_info || {};
    const bank = data.bank_details || {};
    const documents = Array.isArray(data.documents) ? data.documents : [];

    const fullName = [basic.first_name, basic.last_name].filter(Boolean).join(' ') || dash;
    const initials = `${(basic.first_name || '').charAt(0)}${(basic.last_name || '').charAt(0)}`.toUpperCase() || '?';
    const mobileFormatted = basic.mobile_number ? `+91 ${basic.mobile_number}` : '';
    const emailValue = data.email || basic.email || '';
    const primaryRole = userType.user_type_selections?.[0];
    const primaryRoleLabel = primaryRole ? (USER_TYPE_LABEL[primaryRole] || primaryRole) : '';
    const cityState = [basic.city, basic.state].filter(Boolean).join(', ');

    /* "Member since" — only render the pill if the backend gives us a usable
       date. We try a few common paths and fall back to nothing rather than
       inventing a value. */
    const memberSinceRaw = data.created_at || data.user_created_at || basic.created_at || null;
    let memberSinceLabel = '';
    if (memberSinceRaw) {
        const d = new Date(memberSinceRaw);
        if (!isNaN(d.getTime())) {
            memberSinceLabel = d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
        }
    }

    /* Documents list — show all with status icon. Backend gives us the
       array (saved docs); we display each with its label + filename + actions. */
    const docsToShow = documents.map((d) => ({
        id: d.id || d.document_id,
        name: d.document_type || d.document_field_name,
        label: formatDocumentLabel(d.document_type || d.document_field_name),
        file_name: d.file_name || d.original_file_name,
        status: d.status || d.upload_status,
    }));

    const productsCount = (loanAgent.loan_products_handled || []).length;

    return (
        <div className="grid">
            <div className="col-12">
                <Toast ref={toast} />

                {/* ── Page header ─────────────────────────────────── */}
                <div className="mb-3">
                    <h1 className="text-xl font-semibold text-900 m-0 mb-1">Profile</h1>
                    <p className="text-600 text-sm m-0">All your details — saved and ready.</p>
                </div>

                {/* ── Hero card (colorful identity + tinted pills) ───────
                    Pastel diagonal wash + decorative ambient blob,
                    avatar in a 2-color gradient ring, role chip in
                    primary, mixed-tint metadata pills below.
                    Each pill = surface-50/surface-700 of a different
                    semantic color so facts scan independently. */}
                <Card
                    className="shadow-1 mb-3 overflow-hidden"
                    pt={{ body: { className: 'p-0' }, content: { className: 'p-0' } }}
                >
                    <div
                        className="relative p-3 md:p-4"
                        style={{
                            background:
                                'linear-gradient(135deg, var(--primary-50) 0%, var(--blue-50) 100%)',
                        }}
                    >
                        {/* Ambient decorative blob (top-right) */}
                        <div
                            aria-hidden="true"
                            className="absolute"
                            style={{
                                top: '-3rem',
                                right: '-3rem',
                                width: '10rem',
                                height: '10rem',
                                background:
                                    'radial-gradient(circle, var(--primary-200) 0%, transparent 70%)',
                                opacity: 0.5,
                                pointerEvents: 'none',
                            }}
                        />

                        {/* Top row: avatar + name/role + edit */}
                        <div className="relative flex flex-wrap align-items-center gap-3">
                            <div
                                className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                                style={{
                                    width: '4rem',
                                    height: '4rem',
                                    background:
                                        'linear-gradient(135deg, var(--primary-500) 0%, var(--blue-500) 100%)',
                                    padding: '3px',
                                }}
                                aria-hidden="true"
                            >
                                <div
                                    className="flex align-items-center justify-content-center border-circle w-full h-full font-bold"
                                    style={{
                                        backgroundColor: 'var(--surface-card)',
                                        color: 'var(--primary-700)',
                                        fontSize: '1.25rem',
                                    }}
                                >
                                    {initials}
                                </div>
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="text-900 font-bold text-xl white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                    {fullName}
                                </div>
                                {primaryRoleLabel && (
                                    <span
                                        className="inline-flex align-items-center gap-1 border-round-2xl mt-2"
                                        style={{
                                            backgroundColor: 'var(--primary-500)',
                                            color: 'var(--primary-color-text)',
                                            fontSize: '0.7rem',
                                            padding: '0.25rem 0.6rem',
                                            fontWeight: 500,
                                        }}
                                    >
                                        <i
                                            className="pi pi-briefcase"
                                            style={{ fontSize: '0.7rem' }}
                                            aria-hidden="true"
                                        />
                                        {primaryRoleLabel}
                                    </span>
                                )}
                            </div>

                            <Button
                                label="Edit profile"
                                icon="pi pi-pencil"
                                size="small"
                                onClick={() => navigate('/app/profile/complete')}
                                className="flex-shrink-0"
                                pt={{ label: { className: 'hidden md:inline ml-1' } }}
                            />
                        </div>

                        {/* Metadata pill row */}
                        {(cityState || mobileFormatted || emailValue || memberSinceLabel) && (
                            <div className="relative flex flex-wrap gap-2 mt-3">
                                {cityState && (
                                    <HeroPill
                                        icon="pi-map-marker"
                                        value={cityState}
                                        bg="var(--orange-50)"
                                        color="var(--orange-700)"
                                    />
                                )}
                                {mobileFormatted && (
                                    <HeroPill
                                        icon="pi-phone"
                                        value={mobileFormatted}
                                        bg="var(--green-50)"
                                        color="var(--green-700)"
                                    />
                                )}
                                {emailValue && (
                                    <HeroPill
                                        icon="pi-envelope"
                                        value={emailValue}
                                        bg="var(--blue-50)"
                                        color="var(--blue-700)"
                                        maxWidth="14rem"
                                    />
                                )}
                                {memberSinceLabel && (
                                    <HeroPill
                                        icon="pi-calendar"
                                        value={`Member since ${memberSinceLabel}`}
                                        bg="var(--purple-50)"
                                        color="var(--purple-700)"
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </Card>

                {/* ── Personal details ──────────────────────────────── */}
                <SectionPanel
                    icon="pi-user"
                    title="Personal details"
                    subtitle="Identity & contact"
                    onEdit={() => editStep(0)}
                >
                    <DefinitionList>
                        <Definition label="First name" value={basic.first_name} cols="col-6 md:col-3" />
                        <Definition label="Last name" value={basic.last_name} cols="col-6 md:col-3" />
                        <Definition label="Mobile" value={mobileFormatted} cols="col-12 md:col-3" valueIcon="pi-phone" />
                        <Definition label="Email" value={data.email || basic.email} cols="col-12 md:col-3" valueIcon="pi-envelope" />
                    </DefinitionList>
                </SectionPanel>

                {/* ── Address ──────────────────────────────────────── */}
                <SectionPanel
                    icon="pi-map-marker"
                    title="Address"
                    subtitle="Where you're based"
                    onEdit={() => editStep(0)}
                >
                    <DefinitionList>
                        <Definition label="Pincode" value={basic.pincode} cols="col-6 md:col-4" />
                        <Definition label="City" value={basic.city} cols="col-6 md:col-4" />
                        <Definition label="State" value={basic.state} cols="col-6 md:col-4" />
                        {basic.address && (
                            <Definition label="Street address" value={basic.address} fullWidth valueIcon="pi-home" />
                        )}
                    </DefinitionList>
                </SectionPanel>

                {/* ── Role & focus ─────────────────────────────────── */}
                <SectionPanel
                    icon="pi-id-card"
                    title="Role & focus"
                    subtitle="What you do and where"
                    titleAction={
                        productsCount > 0 ? (
                            <Tag
                                value={`${productsCount} product${productsCount !== 1 ? 's' : ''}`}
                                severity="info"
                                className="text-xs"
                                style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem' }}
                            />
                        ) : null
                    }
                    onEdit={() => editStep(1)}
                >
                    <DefinitionList>
                        <Definition label="Defined as" fullWidth>
                            {userType.user_type_selections?.length > 0 ? (
                                <div className="flex flex-wrap gap-1 mt-1">
                                    {userType.user_type_selections.map((v) => (
                                        <Tag
                                            key={v}
                                            value={USER_TYPE_LABEL[v] || v}
                                            severity="info"
                                            className="text-xs"
                                            style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem' }}
                                        />
                                    ))}
                                </div>
                            ) : <span className="text-500 text-sm">{dash}</span>}
                        </Definition>
                        <Definition
                            label="Loan products handled"
                            value={formatList(loanAgent.loan_products_handled, LOAN_PRODUCT_LABEL)}
                            cols="col-12 md:col-6"
                        />
                        <Definition
                            label="Referral products"
                            value={formatList(loanAgent.referral_loan_products, LOAN_PRODUCT_LABEL)}
                            cols="col-12 md:col-6"
                        />
                        <Definition
                            label="Monthly disbursements"
                            value={DISBURSEMENT_LABEL[userType.total_loan_disbursements_per_month] || dash}
                            cols="col-12 md:col-6"
                        />
                        <Definition
                            label="Active areas"
                            value={
                                Array.isArray(loanAgent.active_cities) && loanAgent.active_cities.length > 0
                                    ? `${loanAgent.active_cities.length} ${loanAgent.active_cities.length === 1 ? 'area' : 'areas'}`
                                    : dash
                            }
                            cols="col-6 md:col-3"
                        />
                        <Definition
                            label="Support areas"
                            value={
                                Array.isArray(loanAgent.support_needed_cities) && loanAgent.support_needed_cities.length > 0
                                    ? `${loanAgent.support_needed_cities.length} ${loanAgent.support_needed_cities.length === 1 ? 'area' : 'areas'}`
                                    : dash
                            }
                            cols="col-6 md:col-3"
                        />
                    </DefinitionList>
                </SectionPanel>

                {/* ── Establishment & banking ──────────────────────── */}
                <SectionPanel
                    icon="pi-building"
                    title="Establishment & banking"
                    subtitle="Your business + bank account"
                    titleAction={
                        establishment.has_gst_certificate ? (
                            <Tag
                                value="GST registered"
                                severity="success"
                                icon="pi pi-check"
                                className="text-xs"
                                style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem' }}
                            />
                        ) : null
                    }
                    onEdit={() => editStep(2)}
                >
                    <DefinitionList>
                        <Definition
                            label="Establishment type"
                            cols="col-6 md:col-4"
                        >
                            {establishment.type_of_establishment ? (
                                <Tag
                                    value={ESTABLISHMENT_LABEL[establishment.type_of_establishment] || establishment.type_of_establishment}
                                    severity="info"
                                    icon="pi pi-building"
                                    className="text-xs"
                                    style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem' }}
                                />
                            ) : (
                                <div className="text-400 text-sm">{dash}</div>
                            )}
                        </Definition>
                        {establishment.designation && (
                            <Definition
                                label="Designation"
                                value={establishment.designation}
                                cols="col-6 md:col-4"
                                valueIcon="pi-briefcase"
                            />
                        )}
                        {establishment.has_gst_certificate && establishment.gst_certificate_number && (
                            <Definition
                                label="GSTIN"
                                value={establishment.gst_certificate_number}
                                cols="col-12 md:col-4"
                                valueIcon="pi-id-card"
                            />
                        )}
                        <Definition
                            label="Bank name"
                            value={bank.bank_name}
                            cols="col-12 md:col-4"
                            valueIcon="pi-building"
                        />
                        <Definition
                            label="Account number"
                            value={
                                bank.account_number
                                    ? `•••• ${String(bank.account_number).slice(-4)}`
                                    : ''
                            }
                            cols="col-6 md:col-4"
                            valueIcon="pi-credit-card"
                        />
                        <Definition
                            label="IFSC"
                            value={bank.ifsc_code}
                            cols="col-6 md:col-4"
                            valueIcon="pi-key"
                        />
                    </DefinitionList>
                </SectionPanel>

                {/* ── Documents ────────────────────────────────────── */}
                <SectionPanel
                    icon="pi-file"
                    title="Documents"
                    titleAction={(
                        <Tag
                            value={`${docsToShow.length} ${docsToShow.length === 1 ? 'file' : 'files'}`}
                            severity="secondary"
                            className="text-xs"
                            style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem' }}
                        />
                    )}
                    onEdit={() => editStep(3)}
                >
                    {docsToShow.length === 0 ? (
                        <div className="text-500 text-sm py-2">No documents uploaded yet.</div>
                    ) : (
                        <ul className="list-none p-0 m-0">
                            {docsToShow.map((d, idx) => (
                                <li
                                    key={d.id || d.name || idx}
                                    className={classNames(
                                        'flex flex-wrap align-items-center gap-2 py-2',
                                        idx > 0 ? 'border-top-1 surface-border' : ''
                                    )}
                                >
                                    <i
                                        className="pi pi-check-circle text-green-500 flex-shrink-0"
                                        style={{ fontSize: '0.85rem' }}
                                        aria-hidden="true"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-900 font-medium text-xs white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                            {d.label || d.name}
                                        </div>
                                        {d.file_name && (
                                            <div className="text-500 text-xs white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                                {d.file_name}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex align-items-center gap-1 flex-shrink-0">
                                        <Button
                                            icon="pi pi-eye"
                                            text
                                            rounded
                                            size="small"
                                            tooltip="View"
                                            tooltipOptions={{ position: 'top' }}
                                            onClick={() => handleViewDoc(d)}
                                            loading={docUrlLoading && viewingDoc?.id === d.id}
                                            aria-label={`View ${d.label}`}
                                        />
                                        <Button
                                            icon="pi pi-download"
                                            text
                                            rounded
                                            size="small"
                                            tooltip="Download"
                                            tooltipOptions={{ position: 'top' }}
                                            onClick={() => handleDownloadDoc(d)}
                                            aria-label={`Download ${d.label}`}
                                        />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </SectionPanel>
            </div>

            {/* ── Document viewer modal ─────────────────────────────── */}
            <DocumentViewerDialog
                doc={viewingDoc}
                onHide={() => setViewingDoc(null)}
                onDownload={() => viewingDoc && handleDownloadDoc(viewingDoc)}
            />
        </div>
    );
};

/* ════════════════════════════════════════════════════════════════════
 * HERO PILL — tinted metadata chip used inside the hero card.
 *   - Each pill carries its own semantic color (location/phone/email/date)
 *     so facts read as separate badges instead of a comma-joined run-on.
 *   - `maxWidth` is optional — used for the email pill which can be long;
 *     overflow ellipsis kicks in.
 * ════════════════════════════════════════════════════════════════════ */
const HeroPill = ({ icon, value, bg, color, maxWidth }) => (
    <div
        className="flex align-items-center gap-1 border-round-2xl"
        style={{
            backgroundColor: bg,
            color,
            padding: '0.25rem 0.6rem',
            maxWidth: maxWidth || 'none',
        }}
    >
        <i
            className={`pi ${icon} flex-shrink-0`}
            style={{ fontSize: '0.7rem' }}
            aria-hidden="true"
        />
        <span className="text-xs font-medium white-space-nowrap overflow-hidden text-overflow-ellipsis">
            {value}
        </span>
    </div>
);

/* ════════════════════════════════════════════════════════════════════
 * SECTION PANEL — richer card design
 *   - White card surface with subtle shadow
 *   - 4px primary-color border-left accent (visual anchor per section)
 *   - Stronger header: larger icon chip (2.5rem), bolder title (text-base)
 *   - Status / count pill in header for at-a-glance info
 * ════════════════════════════════════════════════════════════════════ */
const SectionPanel = ({ icon, title, subtitle, titleAction, onEdit, children, className = '' }) => (
    <div
        className={classNames('surface-card border-round shadow-1 mb-3 overflow-hidden', className)}
        style={{ borderLeft: '4px solid var(--primary-color)' }}
    >
        <div className="flex align-items-center gap-3 p-3 pb-2">
            {icon && (
                <div
                    className="flex align-items-center justify-content-center border-round flex-shrink-0"
                    style={{
                        width: '2.5rem',
                        height: '2.5rem',
                        backgroundColor: 'var(--primary-50)',
                        color: 'var(--primary-color)',
                    }}
                >
                    <i className={`pi ${icon}`} style={{ fontSize: '1.05rem' }} aria-hidden="true" />
                </div>
            )}
            <div className="flex-1 min-w-0">
                <div className="flex align-items-center gap-2 flex-wrap">
                    <span className="text-900 font-semibold text-base">{title}</span>
                    {titleAction}
                </div>
                {subtitle && <div className="text-500 text-xs mt-1">{subtitle}</div>}
            </div>
            {onEdit && (
                <Button
                    label="Edit"
                    icon="pi pi-pencil"
                    text
                    size="small"
                    severity="secondary"
                    onClick={onEdit}
                    aria-label={`Edit ${title}`}
                    pt={{
                        root: { style: { padding: '0.25rem 0.5rem', fontSize: '0.75rem' } },
                        label: { className: 'hidden md:inline ml-1' },
                    }}
                />
            )}
        </div>
        <div className="border-top-1 surface-border" />
        <div className="p-3">{children}</div>
    </div>
);

const DefinitionList = ({ children }) => <div className="grid">{children}</div>;

/* Definition — value gets an optional small leading icon for instant
   recognition (mobile, email, address). Empty values render as a muted
   dash. */
const Definition = ({ label, value, children, fullWidth, cols, valueIcon, valueClassName }) => {
    const colsClass = fullWidth ? 'col-12' : (cols || 'col-12 sm:col-6');
    return (
        <div className={classNames('mb-3', colsClass)}>
            <div
                className="text-500 text-xs font-medium mb-1"
                style={{ letterSpacing: '0.04em' }}
            >
                {label}
            </div>
            {children !== undefined ? (
                children
            ) : value ? (
                <div className={classNames('flex align-items-center gap-2', valueClassName)}>
                    {valueIcon && (
                        <i
                            className={`pi ${valueIcon} text-500 flex-shrink-0`}
                            style={{ fontSize: '0.85rem' }}
                            aria-hidden="true"
                        />
                    )}
                    <span className="text-900 text-sm font-medium white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {value}
                    </span>
                </div>
            ) : (
                <div className="text-400 text-sm">{dash}</div>
            )}
        </div>
    );
};

/* ════════════════════════════════════════════════════════════════════
 * DOCUMENT VIEWER DIALOG
 * Replaces the legacy `document.write()` viewer (XSS-vulnerable). Uses a
 * proper PrimeReact Dialog with sandboxed iframe for PDFs and <img> for
 * images. No raw HTML interpolation.
 * ════════════════════════════════════════════════════════════════════ */
const DocumentViewerDialog = ({ doc, onHide, onDownload }) => {
    const fileName = doc?.file_name || '';
    const ext = fileName.split('.').pop()?.toLowerCase();
    const isPdf = ext === 'pdf';
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);

    return (
        <Dialog
            visible={!!doc}
            onHide={onHide}
            header={(
                <div className="flex align-items-center gap-2 min-w-0">
                    <i className="pi pi-file text-primary flex-shrink-0" aria-hidden="true" />
                    <span className="text-900 font-semibold text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {doc?.label || fileName || 'Document'}
                    </span>
                </div>
            )}
            modal
            dismissableMask
            style={{ width: '90vw', maxWidth: '900px' }}
            breakpoints={{ '768px': '95vw' }}
            contentStyle={{ padding: 0 }}
            footer={(
                <div className="flex justify-content-end gap-2">
                    {onDownload && (
                        <Button
                            label="Download"
                            icon="pi pi-download"
                            outlined
                            severity="secondary"
                            size="small"
                            onClick={onDownload}
                        />
                    )}
                    <Button
                        label="Close"
                        icon="pi pi-times"
                        text
                        size="small"
                        onClick={onHide}
                    />
                </div>
            )}
        >
            {doc && (
                <div className="flex align-items-center justify-content-center surface-50" style={{ minHeight: '60vh' }}>
                    {isPdf && (
                        <iframe
                            src={doc.document_url}
                            title={doc.label || fileName}
                            className="border-none w-full"
                            style={{ height: '70vh' }}
                            sandbox="allow-same-origin allow-scripts allow-popups"
                        />
                    )}
                    {isImage && (
                        <img
                            src={doc.document_url}
                            alt={doc.label || fileName}
                            style={{ maxWidth: '100%', maxHeight: '70vh', display: 'block' }}
                        />
                    )}
                    {!isPdf && !isImage && (
                        <div className="flex flex-column align-items-center text-center p-5 gap-2">
                            <i className="pi pi-file pi-2x text-400" aria-hidden="true" />
                            <div className="text-700 text-sm">
                                Preview isn't available for this file type.
                            </div>
                            {onDownload && (
                                <Button
                                    label="Download to view"
                                    icon="pi pi-download"
                                    size="small"
                                    onClick={onDownload}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}
        </Dialog>
    );
};

export default Profile;
